import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, Disc, Droplets, Wrench, Package, Info, ShieldCheck, Truck, Handshake, ChevronDown } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useT } from "@/i18n/helpers";
import Seo from "@/components/Seo";

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);
const Overline = ({ children }) => (<div className="label-overline text-nanda-muted mb-4">{children}</div>);

export default function ProduitsConsommables() {
 const tt = useT();

 const CATEGORIES = [
 { icon: Flame, title: tt("Soudage et assemblage", "Welding and assembly", "Soldadura e montagem"),
 items: [
 tt("Électrodes de soudure", "Welding electrodes", "Eléctrodos de soldadura"),
 tt("Consommables de soudage", "Welding consumables", "Consumíveis de soldadura"),
 tt("Accessoires d'assemblage", "Assembly accessories", "Acessórios de montagem"),
 ] },
 { icon: Disc, title: tt("Abrasifs et découpe", "Abrasives and cutting", "Abrasivos e corte"),
 items: [
 tt("Meules de découpe", "Cutting discs", "Discos de corte"),
 tt("Meules d'ébarbage", "Grinding discs", "Discos de rebarbagem"),
 tt("Disques abrasifs", "Abrasive discs", "Discos abrasivos"),
 ] },
 { icon: Droplets, title: tt("Maintenance frigorifique & climatisation", "Refrigeration & HVAC maintenance", "Manutenção frigorífica e climatização"),
 items: [
 tt("Produits de nettoyage", "Cleaning products", "Produtos de limpeza"),
 tt("Désinfectants pour climatiseurs", "AC disinfectants", "Desinfectantes para climatizadores"),
 tt("Produits d'entretien des échangeurs", "Heat exchanger cleaners", "Produtos de manutenção de permutadores"),
 tt("Produits de maintenance préventive", "Preventive maintenance products", "Produtos de manutenção preventiva"),
 ] },
 { icon: Wrench, title: tt("Outillage professionnel", "Professional tooling", "Ferramenta profissional"),
 items: [
 tt("Outillage spécialisé", "Specialised tools", "Ferramenta especializada"),
 tt("Ventouses de manutention", "Handling suction cups", "Ventosas de manuseio"),
 tt("Visserie technique", "Technical screws", "Parafusaria técnica"),
 tt("Accessoires de montage", "Mounting accessories", "Acessórios de montagem"),
 ] },
 { icon: Package, title: tt("Fournitures techniques", "Technical supplies", "Fornecimentos técnicos"),
 items: [
 tt("Consommables pour installation frigorifique", "Refrigeration installation consumables", "Consumíveis para instalação frigorífica"),
 tt("Consommables pour climatisation", "HVAC consumables", "Consumíveis para climatização"),
 tt("Accessoires pour maintenance", "Maintenance accessories", "Acessórios para manutenção"),
 tt("Fournitures industrielles", "Industrial supplies", "Fornecimentos industriais"),
 ] },
 ];

 const ATOUTS = [
 { i: ShieldCheck, t: tt("Fabricants reconnus", "Recognised manufacturers", "Fabricantes reconhecidos"),
 d: tt("Sélection auprès de fournisseurs internationalement reconnus, produits conformes aux normes en vigueur.",
 "Sourced from internationally recognised suppliers, products compliant with current standards.",
 "Selecção junto de fornecedores reconhecidos internacionalmente, produtos conformes às normas em vigor.") },
 { i: Truck, t: tt("Approvisionnement fiable", "Reliable supply", "Abastecimento fiável"),
 d: tt("Chaîne logistique via Bissau et disponibilité sur les chantiers en Guinée-Bissau.",
 "Supply chain via Bissau and availability on sites in Guinea-Bissau.",
 "Cadeia logística via Bissau, disponibilidade nas obras da Guiné-Bissau.") },
 { i: Handshake, t: tt("Accompagnement B2B", "B2B support", "Acompanhamento B2B"),
 d: tt("Nous aidons nos clients à identifier et commander les consommables les plus adaptés à leurs besoins.",
 "We help our clients identify and order the consumables best suited to their needs.",
 "Ajudamos os nossos clientes a identificar e encomendar os consumíveis mais adaptados às suas necessidades.") },
 { i: Package, t: tt("Gamme élargie sur demande", "Extended range on request", "Gama alargada a pedido"),
 d: tt("Au-delà de cette liste indicative, nous pouvons approvisionner de nombreux autres produits techniques.",
 "Beyond this indicative list, we can source many other technical products.",
 "Para além desta lista indicativa, podemos abastecer muitos outros produtos técnicos.") },
 ];

 return (
 <div data-testid="page-produits-consommables">
 <Seo />
 <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-nanda-ink">
 <img src="/energia-pro-installation.jpg" alt={tt("Produits et consommables professionnels pour la réfrigération et la climatisation", "Professional refrigeration and HVAC supplies and consumables", "Produtos e consumíveis profissionais para refrigeração e climatização")} className="absolute inset-0 h-full w-full object-cover opacity-70" loading="eager" />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/50 via-nanda-ink/30 to-nanda-ink/80" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
 <FadeUp>
 <div className="label-overline text-white/85 mb-6">{tt("NANDA SÁ ENERGIA PRO · Distribution B2B", "NANDA SÁ ENERGIA PRO · B2B distribution", "NANDA SÁ ENERGIA PRO · Distribuição B2B")}</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
 {tt("Produits et consommables", "Professional supplies", "Produtos e consumíveis")}<br />
 <span className="text-nanda-gold italic">{tt("professionnels.", "and consumables.", "profissionais.")}</span>
 </h1>
 <p className="mt-8 max-w-2xl text-white/85 text-lg md:text-xl leading-relaxed">
 {tt("En complément de nos services d'installation, de maintenance et de conseil, NANDA SÁ approvisionne les professionnels du froid, de la climatisation et du génie climatique en produits techniques et consommables sélectionnés auprès de fabricants reconnus.",
 "In addition to our installation, maintenance and consulting services, NANDA SÁ supplies refrigeration, HVAC and climate engineering professionals with technical products and consumables sourced from recognised manufacturers.",
 "Em complemento aos nossos serviços de instalação, manutenção e consultoria, a NANDA SÁ abastece os profissionais do frio, da climatização e da engenharia climática em produtos técnicos e consumíveis seleccionados junto de fabricantes reconhecidos.")}
 </p>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link to="/devis?category=produits-consommables" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-gold hover:text-white transition-colors duration-500" data-testid="pc-cta-devis">
 {tt("Demander un devis", "Request a quote", "Solicitar orçamento")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <a href="#categories" className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="pc-cta-scroll">
 {tt("Voir les catégories", "See categories", "Ver as categorias")} <ChevronDown size={16} strokeWidth={1.5} />
 </a>
 </div>
 </FadeUp>
 </div>
 </section>

 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Notre offre B2B", "Our B2B offer", "A nossa oferta B2B")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Les professionnels du froid, servis par des professionnels du froid.", "Refrigeration professionals, served by refrigeration professionals.", "Os profissionais do frio, servidos por profissionais do frio.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>{tt("Nous connaissons les besoins concrets d'un chantier de réfrigération, d'une opération de maintenance ou d'une intervention en climatisation. C'est pour cette raison que NANDA SÁ propose, en complément de ses services d'installation, une gamme de produits et consommables techniques adaptés au terrain ouest-africain.",
 "We know the concrete needs of a refrigeration project, a maintenance operation or a HVAC intervention. This is why NANDA SÁ offers, alongside its installation services, a range of technical products and consumables adapted to West African field conditions.",
 "Conhecemos as necessidades concretas de uma obra de refrigeração, de uma operação de manutenção ou de uma intervenção em climatização. Por essa razão a NANDA SÁ propõe, em complemento aos seus serviços de instalação, uma gama de produtos e consumíveis técnicos adaptados ao terreno oeste-africano.")}</p>
 <p>{tt("Nos clients sont des installateurs frigoristes, des sociétés de maintenance, des bureaux d'études, des industriels et des collectivités. Chaque référence est sélectionnée pour sa fiabilité et sa disponibilité sur nos routes d'approvisionnement.",
 "Our clients are refrigeration installers, maintenance companies, engineering firms, industrial players and local authorities. Every reference is selected for its reliability and availability on our supply routes.",
 "Os nossos clientes são instaladores frigoristas, empresas de manutenção, gabinetes de estudo, industriais e autarquias. Cada referência é seleccionada pela sua fiabilidade e disponibilidade nas nossas rotas de abastecimento.")}</p>
 <p>{tt("La liste ci-dessous est indicative. NANDA SÁ peut accompagner ses clients dans l'approvisionnement de nombreux autres équipements et consommables professionnels selon les besoins des projets.",
 "The list below is indicative. NANDA SÁ can support its clients in sourcing many other professional equipment and consumables according to project needs.",
 "A lista abaixo é indicativa. A NANDA SÁ pode acompanhar os seus clientes no abastecimento de muitos outros equipamentos e consumíveis profissionais consoante as necessidades dos projectos.")}</p>
 </FadeUp>
 </div>
 </Section>

 <Section className="bg-nanda-bgAlt" id="categories">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Cinq familles de produits", "Five product families", "Cinco famílias de produtos")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Cinq univers techniques, une même exigence.", "Five technical universes, one same standard.", "Cinco universos técnicos, uma mesma exigência.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {CATEGORIES.map((c) => (
 <StaggerItem key={c.title} className="bg-white p-8 md:p-10">
 <c.icon size={30} strokeWidth={1.2} className="text-nanda-terracotta mb-5" />
 <div className="font-serif text-2xl text-nanda-ink leading-tight mb-5">{c.title}</div>
 <ul className="space-y-2.5">
 {c.items.map((it) => (
 <li key={it} className="flex items-start gap-2.5 text-[15px] text-nanda-inkSoft leading-snug">
 <span className="w-1 h-1 rounded-full bg-nanda-terracotta mt-2 shrink-0" />{it}
 </li>
 ))}
 </ul>
 </StaggerItem>
 ))}
 </Stagger>

 <FadeUp delay={0.2} className="mt-10 flex items-start gap-3 max-w-3xl bg-white border border-nanda-line p-6">
 <Info size={20} strokeWidth={1.5} className="text-nanda-terracotta shrink-0 mt-0.5" />
 <p className="text-sm md:text-[15px] text-nanda-inkSoft leading-relaxed">
 <strong className="text-nanda-ink">{tt("Liste indicative.", "Indicative list.", "Lista indicativa.")}</strong>{""}
 {tt("NANDA SÁ peut fournir une large gamme de produits techniques et industriels adaptés aux besoins de ses clients. Contactez-nous pour toute demande spécifique.",
 "NANDA SÁ can supply a wide range of technical and industrial products adapted to its clients' needs. Contact us for any specific request.",
 "A NANDA SÁ pode fornecer uma vasta gama de produtos técnicos e industriais adaptados às necessidades dos seus clientes. Contacte-nos para qualquer pedido específico.")}
 </p>
 </FadeUp>
 </Section>

 <Section>
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Nos atouts", "Our strengths", "As nossas vantagens")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Quatre raisons de nous consulter.", "Four reasons to consult us.", "Quatro razões para nos consultar.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
 {ATOUTS.map((a) => (
 <div key={a.t} className="flex flex-col gap-3">
 <a.i size={24} strokeWidth={1.2} className="text-nanda-terracotta" />
 <div className="font-serif text-xl text-nanda-ink">{a.t}</div>
 <p className="text-sm text-nanda-inkSoft leading-relaxed">{a.d}</p>
 </div>
 ))}
 </div>
 </Section>

 <Section className="bg-nanda-ink text-white">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-nanda-gold">{tt("Nous consulter", "Get in touch", "Consulte-nos")}</span></Overline>
 <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">
 {tt("Besoin de consommables,", "Need consumables,", "Precisa de consumíveis,")}<br />
 <span className="text-nanda-gold italic">{tt("d'outillage ou de fournitures techniques ?", "tooling or technical supplies?", "ferramenta ou fornecimentos técnicos?")}</span>
 </h3>
 <p className="mt-8 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
 {tt("Décrivez votre besoin : type de chantier, quantités, délais souhaités. Nous revenons vers vous avec une proposition adaptée.",
 "Describe your need: type of project, quantities, desired lead times. We come back to you with a tailored proposal.",
 "Descreva a sua necessidade: tipo de obra, quantidades, prazos desejados. Voltaremos com uma proposta adaptada.")}
 </p>
 <div className="mt-12 flex flex-wrap gap-4 justify-center">
 <Link to="/devis?category=produits-consommables" className="inline-flex items-center gap-3 bg-nanda-gold text-white px-10 py-5 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="pc-cta-final-devis">
 {tt("Demander un devis", "Request a quote", "Solicitar orçamento")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <Link to="/contact?subject=Produits-consommables" className="inline-flex items-center gap-3 border border-white/50 text-white px-10 py-5 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="pc-cta-final-contact">
 {tt("Nous consulter pour un besoin spécifique", "Consult us for a specific need", "Consulte-nos para uma necessidade específica")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </div>
 );
}
